import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Zap, Clock, Flame, FastForward, Timer } from 'lucide-react';

interface BoostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBoost: (percentage: number) => void;
  currentDeadline: Date;
}

export default function BoostModal({ isOpen, onClose, onBoost, currentDeadline }: BoostModalProps) {
  const handleBoost = (percentage: number) => {
    onBoost(percentage);
    onClose();
  };

  const calculateNewTime = (percentage: number) => {
    const now = new Date();
    const timeRemaining = currentDeadline.getTime() - now.getTime();
    const reducedTime = timeRemaining * (percentage / 100);
    const newDeadline = new Date(currentDeadline.getTime() - reducedTime);
    return newDeadline.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2">
            <Zap className="text-primary" size={24} />
            Boost Your Task
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Reduce the time remaining to increase pressure and challenge yourself!
          </p>
          
          <div className="space-y-3">
            <Button
              onClick={() => handleBoost(75)}
              variant="outline"
              className="w-full justify-start gap-3 h-auto p-4"
            >
              <div className="flex items-start gap-2">
                <Flame size={16} className="text-red-500 mt-0.5" />
                <div>
                  <div className="font-medium">Super Fast</div>
                  <div className="text-sm text-muted-foreground">-75% time</div>
                </div>
              </div>
              <div className="text-right ml-auto">
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock size={12} />
                  New deadline: {calculateNewTime(75)}
                </div>
              </div>
            </Button>

            <Button
              onClick={() => handleBoost(50)}
              variant="outline"
              className="w-full justify-start gap-3 h-auto p-4"
            >
              <div className="flex items-start gap-2">
                <FastForward size={16} className="text-yellow-500 mt-0.5" />
                <div>
                  <div className="font-medium">Fast</div>
                  <div className="text-sm text-muted-foreground">-50% time</div>
                </div>
              </div>
              <div className="text-right ml-auto">
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock size={12} />
                  New deadline: {calculateNewTime(50)}
                </div>
              </div>
            </Button>

            <Button
              onClick={() => handleBoost(25)}
              variant="outline"
              className="w-full justify-start gap-3 h-auto p-4"
            >
              <div className="flex items-start gap-2">
                <Timer size={16} className="text-blue-500 mt-0.5" />
                <div>
                  <div className="font-medium">Quick</div>
                  <div className="text-sm text-muted-foreground">-25% time</div>
                </div>
              </div>
              <div className="text-right ml-auto">
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock size={12} />
                  New deadline: {calculateNewTime(25)}
                </div>
              </div>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}